/* Cart upsells. Site-wide: removes add-on lines whose main item left the cart (on load, from the
 * mini cart and before checkout). Cart page: up to three squares showing the customer's design on
 * other products; the server supplies previews, titles and confirmed prices. Nothing navigates. */
(() => {
  const root=()=>window.Shopify?.routes?.root||'/';
  const signal=(ms=10000)=>typeof AbortSignal!=='undefined'&&typeof AbortSignal.timeout==='function'?AbortSignal.timeout(ms):undefined;
  const props=item=>item&&item.properties||{};
  async function cart(){const response=await fetch(root()+'cart.js',{credentials:'same-origin',cache:'no-store',signal:signal()});if(!response.ok)throw Error('Cart unavailable');return response.json();}
  async function post(path,body){
    const response=await fetch(root()+path,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify(body),signal:signal()});
    if(!response.ok)throw Error('Cart update failed');return response.json();
  }
  /** Indexes of add-on lines whose parent is not a main line with quantity > 0; quantities override line quantities when given. */
  function orphans(items,quantities){
    const quantity=(item,index)=>quantities?quantities[index]:item.quantity;
    const parents=new Set(items.filter((item,index)=>props(item)._svs_upsell!=='1'&&quantity(item,index)>0).map(item=>props(item)._svs_personalization).filter(Boolean));
    return items.map((_,index)=>index).filter(index=>props(items[index])._svs_upsell==='1'&&quantity(items[index],index)>0&&!parents.has(props(items[index])._svs_upsell_parent));
  }
  let sweeping=null,streak=0;
  /** Removes orphan add-on lines with one cart/update.js call; resolves with the updated cart, or null when nothing changed. */
  function sweep(given){
    if(sweeping)return sweeping;
    sweeping=(async()=>{
      const current=given&&Array.isArray(given.items)?given:await cart(),lines=orphans(current.items);
      if(!lines.length){streak=0;return null;}
      if(++streak>3)return null;
      return post('cart/update.js',{updates:Object.fromEntries(lines.map(index=>[current.items[index].key,0]))});
    })().finally(()=>{sweeping=null;});
    return sweeping;
  }
  window.SVSUpsells={sweep,hasOrphans:given=>!!(given&&Array.isArray(given.items)&&orphans(given.items).length)};
  const cartPage=/\/cart\/?$/.test(location.pathname);
  const initial=sweep().then(updated=>{
    if(!updated)return false;
    if(cartPage&&!orphans(updated.items||[]).length){location.reload();return true;}
    window.dispatchEvent(new CustomEvent('svs:cart-updated'));return false;
  },()=>false);
  // Checkout: remove orphans first. A cart form's positional updates[] also decide which main items remain.
  const restore=[];
  async function guard(form){
    const current=await cart(),inputs=[...form.querySelectorAll('input[name="updates[]"]')],aligned=inputs.length>0&&inputs.length===current.items.length;
    const quantities=aligned?current.items.map((item,index)=>{const value=inputs[index].value.trim();return /^[0-9]+$/.test(value)?Number(value):item.quantity;}):undefined;
    const lines=orphans(current.items,quantities);
    if(!lines.length)return;
    if(aligned){for(const index of lines)inputs[index].value='0';return;}
    await sweep(current);
    for(const input of inputs)if(!input.disabled){input.disabled=true;restore.push(()=>{input.disabled=false;});}
  }
  let resubmitting=null;
  document.addEventListener('submit',event=>{
    const form=event.target,submitter=event.submitter;
    if(form===resubmitting||!(form instanceof HTMLFormElement)||!submitter||submitter.name!=='checkout')return;
    event.preventDefault();event.stopImmediatePropagation();
    if(form.dataset.svsCheckout==='pending')return;
    form.dataset.svsCheckout='pending';
    guard(form).catch(()=>{}).finally(()=>{
      delete form.dataset.svsCheckout;resubmitting=form;
      try{
        if(typeof form.requestSubmit==='function')form.requestSubmit(submitter);
        else{const input=document.createElement('input');input.type='hidden';input.name=submitter.name;input.value=submitter.value||'';form.append(input);restore.push(()=>input.remove());form.submit();}
      }finally{resubmitting=null;}
    });
  },true);
  document.addEventListener('click',event=>{
    const link=event.target instanceof Element?event.target.closest('a[href*="/checkout"]'):null;
    if(!link||event.defaultPrevented||event.button!==0||event.metaKey||event.ctrlKey||event.shiftKey||event.altKey||(link.target&&link.target!=='_self')||link.hasAttribute('download'))return;
    event.preventDefault();
    sweep().catch(()=>null).finally(()=>location.assign(link.href));
  });
  window.addEventListener('pageshow',()=>{for(const undo of restore.splice(0))undo();});

  const host=document.querySelector('[data-svs-upsells-cart]');
  if(!host)return;
  const origin=host.getAttribute('data-svs-origin');
  if(!/^https:\/\/[a-z0-9.-]+$/.test(origin||''))return;
  const endpoint=origin+'/api/shopify-upsells',pending=new Map(),variantPattern=/^gid:\/\/shopify\/ProductVariant\/([1-9][0-9]*)$/;
  const element=(tag,text,kind)=>{const node=document.createElement(tag);if(text!==undefined)node.textContent=text;if(kind)node.className=kind;return node;};
  const button=(text,kind)=>{const node=element('button',text,kind);node.type='button';return node;};
  const token=current=>String(current.token||'').split('?')[0];
  const failure=message=>Object.assign(Error(message),{friendly:true});
  const explain=cause=>cause&&cause.friendly?cause.message:'Couldn’t add this to your cart. Please try again.';
  const uuid=()=>typeof crypto.randomUUID==='function'?crypto.randomUUID():'10000000-1000-4000-8000-100000000000'.replace(/[018]/g,c=>(c^crypto.getRandomValues(new Uint8Array(1))[0]&15>>c/4).toString(16));
  async function call(action,body){
    let response;
    // The server renders product images, so allow it more time than a cart call.
    try{response=await fetch(endpoint,{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json'},body:JSON.stringify({action,...body}),signal:signal(60000)});}
    catch{throw failure('Connection problem. Please try again.');}
    if(response.status===409)throw failure('This product changed. Please refresh the page and try again.');
    if(!response.ok)throw failure(response.status===503?'This product is temporarily unavailable. Please try again soon.':'This product can’t be added right now.');
    return response.json();
  }
  function money(amount,currency){try{return new Intl.NumberFormat(undefined,{style:'currency',currency:currency||'USD'}).format(amount);}catch{return amount.toFixed(2);}}
  // Prices are the shop currency; a visitor browsing in another market currency sees no amount rather than a wrong one.
  function pricesShown(offer){const active=window.Shopify?.currency?.active;return !active||String(active).toUpperCase()===(offer.currencyCode||'USD');}
  function priceLabel(offer){const prices=offer.variants.map(variant=>variant.price),low=Math.min(...prices);return (prices.every(price=>price===low)?'':'From ')+money(low,offer.currencyCode);}
  const valid=offer=>offer&&typeof offer.designId==='string'&&offer.designId&&typeof offer.title==='string'&&
    (offer.publicationVersion===undefined||typeof offer.publicationVersion==='string')&&
    typeof offer.preview==='string'&&/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+=*$/.test(offer.preview)&&
    Array.isArray(offer.variants)&&offer.variants.length>0&&offer.variants.every(variant=>variant&&variantPattern.test(variant.id)&&Number.isFinite(variant.price)&&variant.price>=0);
  let dialog;
  function closeDialog(){if(!dialog)return;if(typeof dialog.close==='function')dialog.close();else dialog.removeAttribute('open');}
  function openDialog(label,...children){
    if(!dialog){dialog=element('dialog',undefined,'svs-upsell-dialog');dialog.addEventListener('click',event=>{if(event.target===dialog)closeDialog();});document.body.append(dialog);}
    const panel=element('div',undefined,'svs-upsell-panel');panel.append(...children);dialog.replaceChildren(panel);dialog.setAttribute('aria-label',label);
    if(typeof dialog.showModal==='function'){if(!dialog.open)dialog.showModal();}else dialog.setAttribute('open','');
  }
  function enlarge(offer){
    const close=button('Close','svs-upsell-close'),image=element('img');close.addEventListener('click',closeDialog);
    image.src=offer.preview;image.alt='Your personalized '+offer.title;
    openDialog(offer.title,close,image,element('h3',offer.title));
  }
  let pickers=0;
  /** Separate choices per option name; older responses without named options fall back to one list of variant labels. */
  function openPicker(offer){
    const optionGroups=Array.isArray(offer.options)&&offer.variants.every(variant=>Array.isArray(variant.options)&&variant.options.length>0)?
      offer.options.filter(option=>option&&typeof option.name==='string'&&Array.isArray(option.values)).map(option=>({key:option.name,legend:option.name,choices:option.values.filter(value=>typeof value==='string').map(value=>[value,value])})).filter(group=>group.choices.length>0):[];
    const named=optionGroups.length>0,groups=named?optionGroups:
      [{key:'',legend:'Choose one',choices:offer.variants.map((variant,index)=>[typeof variant.label==='string'&&variant.label?variant.label:'Choice '+(index+1),variant.id])}];
    const valueOf=(variant,name)=>(variant.options.find(option=>option&&option.name===name)||{}).value;
    const selected=new Map(),id=++pickers,price=element('p',pricesShown(offer)?priceLabel(offer):'','svs-upsell-picker-price'),error=element('p',undefined,'svs-upsell-error');
    const confirm=button('Add to cart','svs-upsell-add'),cancel=button('Cancel','svs-upsell-cancel'),actions=element('div',undefined,'svs-upsell-actions');
    error.setAttribute('role','alert');actions.append(confirm,cancel);cancel.addEventListener('click',closeDialog);
    let chosen=null;
    const update=()=>{
      const complete=groups.every(group=>selected.has(group.key));
      chosen=!complete?null:named?offer.variants.find(variant=>groups.every(group=>valueOf(variant,group.key)===selected.get(group.key)))||null:offer.variants.find(variant=>variant.id===selected.get(''))||null;
      price.textContent=!pricesShown(offer)?'':chosen?money(chosen.price,offer.currencyCode):priceLabel(offer);
      error.textContent=complete&&!chosen?'This combination isn’t available.':'';confirm.disabled=!chosen;
    };
    const fieldsets=groups.map((group,index)=>{
      const fieldset=element('fieldset',undefined,'svs-upsell-choice'),values=element('div',undefined,'svs-upsell-values');fieldset.append(element('legend',group.legend),values);
      for(const [label,value] of group.choices){
        const wrap=element('label'),input=element('input');input.type='radio';input.name='svs-upsell-'+id+'-'+index;input.value=value;
        if(group.choices.length===1){input.checked=true;selected.set(group.key,value);}
        input.addEventListener('change',()=>{if(input.checked){selected.set(group.key,value);update();}});
        wrap.append(input,element('span',label));values.append(wrap);
      }
      return fieldset;
    });
    confirm.addEventListener('click',()=>{
      if(!chosen)return;
      confirm.disabled=true;confirm.textContent='Adding…';error.textContent='';
      addVariant(offer,chosen).catch(cause=>{error.textContent=explain(cause);confirm.disabled=false;confirm.textContent='Add to cart';});
    });
    update();openDialog('Choose options for '+offer.title,element('h3',offer.title),...fieldsets,price,error,actions);
  }
  async function addVariant(offer,variant){
    const reference=offer.parentReference,numeric=(variant.id.match(variantPattern)||[])[1];
    if(!numeric)throw failure('This product can’t be added right now.');
    let fresh;try{fresh=await cart();}catch{throw failure('Your cart couldn’t be loaded. Please try again.');}
    if(!fresh.items.some(item=>props(item)._svs_personalization===reference&&props(item)._svs_upsell!=='1'))throw failure('Your original design is no longer in your cart.');
    const key=reference+':'+offer.designId+':'+variant.id,attempt=pending.get(key)||{submissionId:uuid()};pending.set(key,attempt);
    if(!attempt.reference){
      const saved=await call('add',{reference,cartToken:token(fresh),designId:offer.designId,variantId:variant.id,submissionId:attempt.submissionId,publicationVersion:offer.publicationVersion});
      if(!saved||typeof saved.reference!=='string'||!saved.reference)throw failure('This product can’t be added right now.');
      attempt.reference=saved.reference;
      attempt.properties=Object.fromEntries(Object.entries(saved.properties&&typeof saved.properties==='object'?saved.properties:{}).filter(([,value])=>typeof value==='string'));
    }
    if(!fresh.items.some(item=>props(item)._svs_personalization===attempt.reference)){
      try{await post('cart/add.js',{items:[{id:Number(numeric),quantity:1,properties:{...attempt.properties,_svs_personalization:attempt.reference,_svs_upsell:'1',_svs_upsell_parent:reference,_svs_upsell_design_id:offer.designId}}]});}
      catch{throw failure('Couldn’t add this to your cart. Please try again.');}
    }
    location.reload();
  }
  function quantity(offer,line,error){
    const wrap=element('div',undefined,'svs-upsell-line'),box=element('div',undefined,'svs-upsell-qty'),minus=button('−'),plus=button('+'),count=element('span',String(line.quantity));
    const variant=offer.variants.find(item=>item.id==='gid://shopify/ProductVariant/'+line.variant_id),label=offer.variants.length>1?(variant&&variant.label)||line.variant_title||'':'';
    box.setAttribute('role','group');box.setAttribute('aria-label','Quantity for '+offer.title+(label?', '+label:''));
    minus.setAttribute('aria-label','Decrease quantity');plus.setAttribute('aria-label','Increase quantity');count.setAttribute('aria-live','polite');
    const set=async next=>{
      minus.disabled=plus.disabled=true;error.textContent='';
      try{await post('cart/change.js',{id:line.key,quantity:next});location.reload();}
      catch{error.textContent='Couldn’t update the quantity. Please try again.';minus.disabled=plus.disabled=false;}
    };
    minus.addEventListener('click',()=>set(Math.max(0,line.quantity-1)));plus.addEventListener('click',()=>set(line.quantity+1));
    box.append(minus,count,plus);if(label)wrap.append(element('p',label,'svs-upsell-variant'));wrap.append(box);return wrap;
  }
  function card(offer,current){
    const reference=offer.parentReference,article=element('article',undefined,'svs-upsell-card'),media=element('div',undefined,'svs-upsell-media');
    const zoom=button(undefined,'svs-upsell-zoom'),image=element('img'),error=element('p',undefined,'svs-upsell-error');
    zoom.setAttribute('aria-label','Enlarge '+offer.title);image.src=offer.preview;image.alt='Your personalized '+offer.title;image.width=240;image.height=240;image.decoding='async';
    zoom.addEventListener('click',()=>enlarge(offer));zoom.append(image);media.append(zoom);if(pricesShown(offer))media.append(element('span',priceLabel(offer),'svs-upsell-price'));
    error.setAttribute('role','alert');article.append(media,element('h3',offer.title,'svs-upsell-title'));
    const lines=current.items.filter(item=>{const p=props(item);return p._svs_upsell==='1'&&p._svs_upsell_parent===reference&&p._svs_upsell_design_id===offer.designId;});
    if(lines.length)for(const line of lines)article.append(quantity(offer,line,error));
    else{
      const add=button('Add to cart','svs-upsell-add');
      add.addEventListener('click',()=>{
        error.textContent='';
        if(offer.variants.length>1){openPicker(offer);return;}
        add.disabled=true;add.textContent='Adding…';
        addVariant(offer,offer.variants[0]).catch(cause=>{error.textContent=explain(cause);add.disabled=false;add.textContent='Add to cart';});
      });
      article.append(add);
    }
    article.append(error);return article;
  }
  async function show(){
    const current=await cart(),parents=[];
    for(const item of current.items){const reference=props(item)._svs_personalization;if(reference&&props(item)._svs_upsell!=='1'&&item.quantity>0&&!parents.includes(reference))parents.push(reference);}
    host.replaceChildren();if(!parents.length)return;
    const offers=[],cartToken=token(current);
    for(const reference of parents){
      try{
        const data=await call('offers',{reference,cartToken}),list=Array.isArray(data&&data.offers)?data.offers.slice().sort((a,b)=>(Number(a&&a.slot)||0)-(Number(b&&b.slot)||0)):[];
        for(const offer of list)if(offers.length<3&&valid(offer)&&!offers.some(item=>item.parentReference===reference&&item.designId===offer.designId))offers.push({...offer,parentReference:reference});
      }catch{/* A stale source line does not hide other eligible products. */}
      if(offers.length>=3)break;
    }
    if(!offers.length)return;
    const grid=element('div',undefined,'svs-upsell-grid');
    for(const offer of offers)grid.append(card(offer,current));
    host.append(element('h2','Your design on more products','svs-upsell-heading'),grid);
  }
  initial.then(reloading=>reloading?undefined:show()).catch(()=>{host.replaceChildren();});
})();
